"use client";
import { ComboBox } from "cy-ui/components/Combobox";
import { ComboboxOption } from "@headlessui/react";
import { CheckIcon, ChevronDown } from "lucide-react";
import { Flag } from "cy-remittance/components/flags";
import { cn } from "cy-fe/src/utils/cn";
import type { Nil } from "cy-util";

/**
 * Represents a country option for the issuing country combobox.
 */
interface IssuingCountryOption {
  /** Country code (ISO 3166-1 alpha-2, e.g., "ZA", "US") */
  id: string;
  /** Full country name (e.g., "South Africa") */
  name: string;
  /** Country code for the Flag component */
  countryCode: string;
}

/**
 * Props for the IssuingCountryComboBox component.
 */
interface IssuingCountryComboBoxProps {
  /** Array of available country options */
  options: IssuingCountryOption[];
  /** Currently selected country code or null */
  value: string | null;
  /** Callback function called when a country is selected */
  onChange: (countryCode: string | null) => void;
  /** Whether the field has a validation error */
  error?: boolean;
  /** Test ID for testing purposes */
  "data-testid"?: string;
}

/**
 * IssuingCountryComboBox - Country Selection Component with Flags
 *
 * This component provides a searchable dropdown for selecting an issuing country
 * with visual flag icons. It wraps the shared ComboBox component from cy-ui.
 *
 * Key Features:
 * - Multi-word search functionality across country code and name
 * - Visual display with country flags using circular 1x1 aspect ratio
 * - Keyboard navigation and accessibility features
 * - Error state styling
 *
 * Usage:
 * ```tsx
 * <IssuingCountryComboBox
 *   value={selectedCountryCode}
 *   onChange={handleCountryChange}
 *   options={countryOptions}
 *   error={hasError}
 * />
 * ```
 */
export function IssuingCountryComboBox({
  options,
  value,
  onChange,
  error,
  "data-testid": dataTestId,
}: IssuingCountryComboBoxProps) {
  const selectedOption = options.find((opt) => opt.id === value);

  const handleComboChange = (next: IssuingCountryOption | null) => {
    onChange(next?.id || null);
  };

  /**
   * Filters countries based on search query using multi-word matching logic.
   * Searches across country code and country name fields.
   *
   * @param query - The search string entered by the user
   * @returns Filtered array of countries matching the search criteria
   */
  const getOptions = (query: string): IssuingCountryOption[] => {
    // Handle empty or whitespace-only queries
    if (!query?.trim()) {
      return options || [];
    }

    // Ensure options array exists to prevent runtime errors
    if (!options || options.length === 0) {
      return [];
    }

    return options.filter((option) => {
      const searchableText =
        `${option.countryCode} ${option.name}`.toUpperCase();
      const parts = query
        .toUpperCase()
        .split(/ +/)
        .filter((part) => part.length > 0);

      // All search terms must be found in the searchable text
      return parts.every((part) => searchableText.includes(part));
    });
  };

  /**
   * Renders country options with flag and country name.
   *
   * @param filteredOptions - Array of country options to render
   * @returns JSX element containing the rendered country options
   */
  const renderItems = (filteredOptions: IssuingCountryOption[]) => {
    // Handle empty options array gracefully
    if (!filteredOptions || filteredOptions.length === 0) {
      return (
        <div className="px-3 py-3 text-center text-sm text-gray-500">
          No countries found
        </div>
      );
    }

    return (
      <div>
        {filteredOptions.map((option) => {
          return (
            <ComboboxOption key={option.id} value={option} className="py-1">
              {({ focus, selected }) => (
                <div
                  className={cn(
                    "relative flex items-center py-3",
                    "rounded px-3 pr-9 ring-black hover:ring-1",
                    "focus:ring-1",
                    focus && "ring-1",
                  )}
                >
                  <Flag
                    aspect="1x1"
                    className="w-6 rounded-full"
                    country={option.countryCode}
                  />
                  <span className="ml-2">{option.name}</span>

                  {selected && (
                    <CheckIcon className="absolute right-2 h-4 w-4 stroke-green-700" />
                  )}
                </div>
              )}
            </ComboboxOption>
          );
        })}
      </div>
    );
  };

  /**
   * Renders the button content showing the selected country or placeholder.
   *
   * @param val - The currently selected country option or null/undefined
   * @returns JSX element for the button content
   */
  const buttonChildren = (val: IssuingCountryOption | Nil) => {
    return (
      <div className="flex w-full items-center justify-between">
        {val ? (
          <div className="flex items-center">
            <Flag
              aspect="1x1"
              className="w-6 rounded-full"
              country={val.countryCode}
            />
            <span className="ml-2">{val.name}</span>
          </div>
        ) : (
          <span className="text-gray-500">Select country...</span>
        )}
        <ChevronDown className="h-4 w-4 stroke-gray-500" />
      </div>
    );
  };

  return (
    <ComboBox
      value={selectedOption || null}
      onChange={handleComboChange}
      getOptions={getOptions}
      renderItems={renderItems}
      buttonChildren={buttonChildren}
      buttonClassName="flex w-full items-center justify-between border px-3 py-2 rounded"
      error={error}
      panelClassName="min-w-[20rem] max-w-[75vw]"
      aria-label="Select issuing country"
      data-testid={dataTestId}
    />
  );
}
